import { Link, useLocation, useNavigate } from '@tanstack/react-router';
import { clsx } from 'clsx';
import { Fragment, useCallback } from 'react';
import { Menu, Transition } from '@headlessui/react';
const { Button: MenuButton, Items: MenuItems, Item: MenuItem } = Menu;

import { useUIStore, useAuthStore } from '@/stores';
import { useTour } from '@/features/onboarding';
import { clearDismissed } from '@/features/onboarding/utils/dismiss-flag';
import { CURRENT_TOUR_VERSION } from '@/features/onboarding/steps/tour-steps';
import {
  ClipboardDocumentListIcon,
  Cog6ToothIcon,
  Bars3Icon,
  ChevronLeftIcon,
  UserCircleIcon,
  UserIcon,
  CogIcon,
  ArrowRightOnRectangleIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  BanknotesIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline';

const DocumentFoldedIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth="1.5"
    stroke="currentColor"
    className={className}
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"
    />
  </svg>
);

interface NavItem {
  name: string;
  href: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  enabled: boolean;
  /** Per-icon nudge for glyphs heroicons doesn't centre in their own viewBox. */
  iconClassName?: string;
}

const navigation: NavItem[] = [
  { name: 'Inventory', href: '/inventory', icon: ClipboardDocumentListIcon, enabled: true },
  { name: 'Trades', href: '/trades', icon: DocumentFoldedIcon, enabled: true },
  { name: 'Registers', href: '/counterparties', icon: UserGroupIcon, enabled: true },
  // BanknotesIcon is drawn low inside its own 24u viewBox: top gap 4.50 against
  // 2.25-3.00 for every other icon here, so stacked in the rail its glyph starts
  // visibly below the column. Nudge it up by 1px (24u box → 1u). Whole pixel on
  // purpose: the exact centring offset is 0.70px, but these are 1.5px-stroke
  // outline glyphs and a fractional shift lands the horizontals on half-pixels
  // and blurs them, so crispness wins the remaining 0.3px.
  {
    name: 'Collateral Manager',
    href: '/collateral',
    icon: BanknotesIcon,
    iconClassName: '-translate-y-px',
    enabled: true,
  },
  { name: 'Billing', href: '/billing', icon: CurrencyDollarIcon, enabled: true },
];

export const Sidebar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const sidebarCollapsed = useUIStore((state) => state.sidebarCollapsed);
  const toggleSidebar = useUIStore((state) => state.toggleSidebar);
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const { start: startTour } = useTour();

  // Manual "Take a tour" click clears the per-version dismissed flag before
  // starting the tour. Without this, a user whose previous PATCH failed has
  // localStorage still pinning the flag to '1' — the next session's auto-
  // start would skip them silently. Manual re-trigger should always fire.
  //
  // Note: this always starts the FULL tour. The whats-new mini-tour is only
  // reachable via WhatsNewBanner; we intentionally don't hijack the sidebar
  // affordance for it because users who click "Take a tour" want a refresher
  // on the whole product, not just what changed since they were last here.
  const handleTakeTour = useCallback(() => {
    clearDismissed(CURRENT_TOUR_VERSION);
    startTour();
  }, [startTour]);

  // What's-new indicator: small dot on the QuestionMarkCircle icon when
  // the user has seen a previous tour version but not the current one.
  // Disappears naturally as soon as `onboardingSeenVersion` flips to
  // CURRENT_TOUR_VERSION (banner Dismiss path or whats-new tour
  // completion) — no separate "banner-dismissed" coupling required.
  const hasWhatsNew =
    user != null &&
    user.onboardingSeenVersion > 0 &&
    user.onboardingSeenVersion < CURRENT_TOUR_VERSION;

  const handleLogout = async () => {
    await logout();
    navigate({ to: '/login' });
  };

  return (
    <>
      {/* Sidebar */}
      <div className={clsx(
        'fixed inset-y-0 z-[9999] flex flex-col transition-all duration-300',
        sidebarCollapsed ? 'w-18' : 'w-64'
      )}>
        <div
          className="flex grow flex-col gap-y-5 overflow-y-auto bg-white px-6 pb-4 shadow-lg overflow-visible border-r border-gray-200"
        >
          <div className="flex h-16 shrink-0 items-center justify-between">
            <h1 className={clsx(
              'text-xl font-bold text-gray-900 transition-all duration-200 delay-200 ease-in-out',
              sidebarCollapsed ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100 w-auto'
            )}>
              LendRail
            </h1>
            <button
              type="button"
              className="-m-1 p-1 text-gray-400 hover:text-gray-500"
              onClick={toggleSidebar}
            >
              <span className="sr-only">Toggle sidebar</span>
              {sidebarCollapsed ? (
                <Bars3Icon className="h-6 w-6 stroke-1" aria-hidden="true" />
              ) : (
                <ChevronLeftIcon className="h-4 w-4 stroke-2" aria-hidden="true" />
              )}
            </button>
          </div>
          
          <nav className="flex flex-1 flex-col">
            <ul role="list" className="flex flex-1 flex-col gap-y-7">
              <li>
                <ul role="list" className="-mx-2 space-y-1">
                  {navigation.map((item) => {
                    const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/');
                    const isDisabled = !item.enabled;
                    return (
                      <li key={item.name}>
                        {isDisabled ? (
                          <div
                            className={clsx(
                              'text-gray-400 cursor-not-allowed',
                              'group flex rounded-md p-2 text-sm leading-6 font-semibold',
                              sidebarCollapsed ? 'justify-center' : 'gap-x-3'
                            )}
                            title={sidebarCollapsed ? `${item.name} (Disabled)` : undefined}
                          >
                            <div className="flex h-6 w-6 items-center justify-center">
                              <item.icon
                                className={clsx(
                                  'h-6 w-6 shrink-0 text-gray-300',
                                  item.iconClassName
                                )}
                                aria-hidden="true"
                              />
                            </div>
                            {/* `w-0 overflow-hidden` hides the label horizontally but does
                                NOT stop it from laying out: a multi-word name wraps one word
                                per line and each line still contributes leading-6 to the flex
                                row. "Collateral Manager" made its collapsed row 64px instead
                                of 40px, breaking the icon rail's rhythm.
                                `whitespace-nowrap` is unconditional on purpose — the rail
                                is also mid-wrap during the 300ms w-18→w-64 expand, where the
                                label is already `w-auto` but the sidebar is still narrow, so
                                a state-scoped nowrap just moves the 40→64→40 jump into the
                                transition instead of removing it. */}
                            <span className={clsx(
                              'whitespace-nowrap transition-all duration-200 delay-200 ease-in-out',
                              sidebarCollapsed ? 'opacity-0 w-0 overflow-hidden' : 'opacity-50 w-auto'
                            )}>
                              {item.name}
                            </span>
                          </div>
                        ) : (
                          <Link
                            to={item.href}
                            className={clsx(
                              isActive
                                ? 'bg-primary-50 text-primary-700'
                                : 'text-gray-700 hover:text-primary-700 hover:bg-gray-50',
                              'group flex rounded-md p-2 text-sm leading-6 font-semibold transition-colors duration-150',
                              sidebarCollapsed ? 'justify-center' : 'gap-x-3'
                            )}
                            title={sidebarCollapsed ? item.name : undefined}
                          >
                            <div className="flex h-6 w-6 items-center justify-center">
                              <item.icon
                                className={clsx(
                                  isActive
                                    ? 'text-primary-700'
                                    : 'text-gray-400 group-hover:text-primary-700',
                                  'h-6 w-6 shrink-0 transition-colors duration-150',
                                  item.iconClassName,
                                )}
                                aria-hidden="true"
                              />
                            </div>
                            {/* whitespace-nowrap: see the disabled branch above. */}
                            <span className={clsx(
                              'whitespace-nowrap transition-all duration-200 delay-200 ease-in-out',
                              sidebarCollapsed ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100 w-auto'
                            )}>
                              {item.name}
                            </span>
                          </Link>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </li>
              
              <li className="mt-auto -mx-2 space-y-1">
                <button
                  type="button"
                  onClick={handleTakeTour}
                  data-tour="sidebar-take-a-tour"
                  className={clsx(
                    'text-gray-700 hover:text-primary-700 hover:bg-gray-50',
                    // Suppress the browser's default ~2px solid-blue focus
                    // outline; mirror the subtle hover treatment when the
                    // button is focused via keyboard so a11y stays intact
                    // without the loud default ring stealing attention from
                    // the tour spotlight.
                    'focus:outline-none focus-visible:bg-gray-50 focus-visible:text-primary-700',
                    'group flex rounded-md p-2 text-sm leading-6 font-semibold transition-colors duration-150',
                    // Pin to 40x40 when collapsed — matches the nav-item
                    // hover footprint (Inventory/Trades/Counterparties).
                    // The hidden span with `w-0 overflow-hidden` flowed
                    // its text vertically inside `<button>` (different
                    // wrapping behaviour than the `<a>` Link), stretching
                    // the row to ~80px tall; explicit `h-10 w-10` avoids
                    // relying on intrinsic flex sizing here.
                    //
                    // Expanded: `w-full` so the hover rectangle fills the
                    // row to the same right edge as the nav-Links above
                    // — `<button>` shrinks to content by default (unlike
                    // `<a>` with `display:flex`, which fills the parent),
                    // making the hover bar end abruptly after the label.
                    sidebarCollapsed ? 'h-10 w-10 justify-center' : 'w-full gap-x-3'
                  )}
                  title={
                    hasWhatsNew
                      ? 'New features available'
                      : sidebarCollapsed
                        ? 'Take a tour'
                        : undefined
                  }
                >
                  <div className="relative flex h-6 w-6 items-center justify-center">
                    <QuestionMarkCircleIcon
                      className="h-6 w-6 shrink-0 text-gray-400 group-hover:text-primary-700 transition-colors duration-150"
                      aria-hidden="true"
                    />
                    {hasWhatsNew && (
                      <span
                        data-testid="sidebar-whats-new-dot"
                        role="img"
                        aria-label="New features available"
                        className="absolute right-0 top-0 h-2 w-2 rounded-full bg-primary-500 ring-2 ring-white"
                      />
                    )}
                  </div>
                  {/* whitespace-nowrap for the same reason as the nav labels: the
                      h-10 pin above only holds while collapsed, so mid-expand this
                      3-word label would wrap and stretch the row. */}
                  <span className={clsx(
                    'whitespace-nowrap transition-all duration-200 delay-200 ease-in-out',
                    sidebarCollapsed ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100 w-auto'
                  )}>
                    Take a tour
                  </span>
                </button>
                <div
                  className={clsx(
                    'text-gray-400 cursor-not-allowed',
                    'group flex rounded-md p-2 text-sm font-semibold leading-6',
                    sidebarCollapsed ? 'justify-center' : 'gap-x-3'
                  )}
                  title={sidebarCollapsed ? 'Settings (Disabled)' : undefined}
                >
                  <div className="flex h-6 w-6 items-center justify-center">
                    <Cog6ToothIcon
                      className="h-6 w-6 shrink-0 text-gray-300"
                      aria-hidden="true"
                    />
                  </div>
                  <span className={clsx(
                    'transition-all duration-200 delay-200 ease-in-out',
                    sidebarCollapsed ? 'opacity-0 w-0 overflow-hidden' : 'opacity-50 w-auto'
                  )}>
                    Settings
                  </span>
                </div>
              </li>
            </ul>
          </nav>

          {/* Divider */}
          <div className="border-t border-gray-200" />

          {/* User menu */}
          <div className="overflow-visible">
            <Menu as="div" className="relative overflow-visible">
              <MenuButton className={clsx(
                'group flex w-full items-center rounded-md p-2 text-sm font-semibold leading-6 text-gray-700 hover:bg-gray-50 hover:text-primary-700',
                sidebarCollapsed ? 'w-10 justify-center' : '-mx-2 gap-x-3'
              )}>
                <span className="sr-only">Open user menu</span>
                <div className="flex h-6 w-6 items-center justify-center">
                  <UserCircleIcon className="h-6 w-6 shrink-0 text-gray-400 group-hover:text-primary-700" />
                </div>
                <div className={clsx(
                  'flex-1 min-w-0 text-left transition-all duration-200 delay-200 ease-in-out',
                  sidebarCollapsed ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100 w-auto'
                )}>
                  <span className="block truncate text-sm font-semibold text-gray-900">
                    {user?.name || 'User'}
                  </span>
                  <span className="block truncate text-xs text-gray-500">
                    {user?.email || 'user@example.com'}
                  </span>
                </div>
              </MenuButton>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <MenuItems className={clsx(
                  'fixed z-[9999] rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none',
                  sidebarCollapsed 
                    ? 'bottom-14 left-10 w-48' 
                    : 'bottom-18 left-8 w-48'
                )}>
                  <MenuItem disabled>
                    {() => (
                      <div
                        className={clsx(
                          'flex items-center gap-3 px-3 py-2 text-sm leading-6 text-gray-400 cursor-not-allowed'
                        )}
                      >
                        <UserIcon className="h-4 w-4 shrink-0 stroke-2" />
                        Your profile
                      </div>
                    )}
                  </MenuItem>
                  <MenuItem disabled>
                    {() => (
                      <div
                        className={clsx(
                          'flex items-center gap-3 px-3 py-2 text-sm leading-6 text-gray-400 cursor-not-allowed'
                        )}
                      >
                        <CogIcon className="h-4 w-4 shrink-0 stroke-2" />
                        Settings
                      </div>
                    )}
                  </MenuItem>
                  <MenuItem>
                    {({ active }) => (
                      <button
                        onClick={handleLogout}
                        className={clsx(
                          active ? 'bg-gray-50' : '',
                          'flex w-full items-center gap-3 px-3 py-2 text-sm leading-6 text-gray-900 hover:bg-gray-50'
                        )}
                      >
                        <ArrowRightOnRectangleIcon className="h-4 w-4 shrink-0 stroke-2" />
                        Sign out
                      </button>
                    )}
                  </MenuItem>
                </MenuItems>
              </Transition>
            </Menu>
          </div>
        </div>
      </div>
    </>
  );
};
